import { isCaddieGamesHost } from "./isCaddieGamesHost";
import { isIqVerityHost } from "./isIqVerityHost";
import { isMapleCoinHost } from "./isMapleCoinHost";
import { isTospHost } from "./isTospHost";

// Frontend brand registry — the UI twin of the backend's config/brands. A
// non-default brand is active on its custom domain or on one of the paths it
// owns; everything else is the default AskSmart experience.

export type AppContext =
  | "asksmart"
  | "caddiegames"
  | "iqverity"
  | "tosp"
  | "maplecoin";

export interface FrontendBrandConfig {
  brand: AppContext;
  isDefault: boolean;
  // Custom-domain test (prod); brands without a domain rely on their paths.
  matchesHost?: (hostname: string) => boolean;
  // Backend login door; LoginGate appends the platform/force query params.
  authEndpoint: string;
  // Where an unauthenticated visit to this brand's routes bounces.
  loginPath: string;
  // Post-login landing (AuthRedirect route).
  successPath: string;
  loadingText: string;
  // Post-session shortcut: an entry via this brand whose org has the feature
  // goes straight to moduleRoute; everyone else lands like AskSmart.
  entitlementFeatureKey?: string;
  moduleRoute?: string;
  // Does the brand own its module route, or only its two portal paths?
  //
  // This used to be implicit in the shape of a `pathPrefix` string, where a
  // trailing slash was the difference between the two meanings. That was too
  // subtle to be safe — both answers are load-bearing and neither is a default:
  //
  //  - TRUE (CaddieGames, IQVerity, MapleCoin): after login every brand lands on the
  //    SHARED AskSmart frontend, so a signed-in CaddieGames user is standing on
  //    app.asksmart.ai/caddiegames. Claiming the module route is the only thing
  //    keeping them in their brand there — drop it and their "Switch Org" goes
  //    to the AskSmart door, whose picker excludes every caddieGames-flagged
  //    org, i.e. an empty list.
  //  - FALSE (TOSP): /tosp predates the portal and serves regular AskSmart
  //    orgs. Claiming it would do the mirror-image damage — sending those users'
  //    Switch Org, logout and unauthenticated bounce to the TOSP door, whose
  //    picker is empty for them.
  //
  // The deciding question is whether "entitled to the module" implies "flagged
  // as this brand's org" in Auth0. Every portal now syncs its flag from the
  // entitlement automatically (backend utils/brandOrgFlagSync), so the implication
  // holds — which is what makes TRUE safe for CaddieGames and IQVerity.
  //
  // TOSP stays FALSE until its orgs are actually flagged. The sync only fires on
  // an entitlement write, so orgs entitled before it shipped stay unflagged until
  // the backfill (src/migrateBrandOrgFlags.ts) runs. Claiming /tosp ahead of that
  // would strand exactly the users the FALSE is there to protect.
  claimsModuleRoute?: boolean;
}

export const FRONTEND_BRANDS: readonly FrontendBrandConfig[] = [
  {
    brand: "asksmart",
    isDefault: true,
    authEndpoint: "/auth",
    loginPath: "/",
    successPath: "/auth-success",
    loadingText: "Connecting to AskSmart...",
  },
  {
    brand: "caddiegames",
    isDefault: false,
    matchesHost: isCaddieGamesHost,
    authEndpoint: "/auth-caddiegames",
    loginPath: "/caddiegames/login",
    successPath: "/caddiegames/auth-success",
    loadingText: "Connecting to CaddieGames...",
    entitlementFeatureKey: "caddieGames",
    moduleRoute: "/caddiegames",
    claimsModuleRoute: true,
  },
  {
    brand: "iqverity",
    isDefault: false,
    matchesHost: isIqVerityHost,
    authEndpoint: "/auth-iq",
    loginPath: "/iqverity/login",
    successPath: "/iqverity/auth-success",
    loadingText: "Connecting to IQ Verity...",
    entitlementFeatureKey: "iQVerity",
    moduleRoute: "/iqverity",
    claimsModuleRoute: true,
  },
  {
    brand: "tosp",
    isDefault: false,
    matchesHost: isTospHost,
    authEndpoint: "/auth-tosp",
    loginPath: "/tosp/login",
    successPath: "/tosp/auth-success",
    loadingText: "Accessing your Tenant Portal...",
    // Live module catalog key — derived with only the first character
    // lowercased, so "tOSP", not "tosp".
    entitlementFeatureKey: "tOSP",
    moduleRoute: "/tosp",
    claimsModuleRoute: false,
  },
  {
    brand: "maplecoin",
    isDefault: false,
    matchesHost: isMapleCoinHost,
    authEndpoint: "/auth-maplecoin",
    loginPath: "/maplecoin/login",
    successPath: "/maplecoin/auth-success",
    loadingText: "Connecting to MapleCoin...",
    entitlementFeatureKey: "mapleCoin",
    moduleRoute: "/maplecoin",
    claimsModuleRoute: true,
  },
];

export function getBrandConfig(context: AppContext): FrontendBrandConfig {
  const found = FRONTEND_BRANDS.find((b) => b.brand === context);
  // Unreachable for a well-typed context; fall back rather than crash login.
  if (!found) return FRONTEND_BRANDS.find((b) => b.isDefault)!;
  return found;
}

// Compare paths case-insensitively and without a trailing slash, so "/tosp/"
// and "/TOSP" are the same route as "/tosp" — otherwise a stray slash silently
// lands the visitor in a different brand than the one they navigated to.
function normalizePath(path: string): string {
  const lower = path.toLowerCase();
  return lower.length > 1 && lower.endsWith("/") ? lower.slice(0, -1) : lower;
}

/** Does `path` belong to this brand's portal? */
function ownsPath(brand: FrontendBrandConfig, path: string): boolean {
  // The portal's own two pages are always the brand's.
  if (normalizePath(brand.loginPath) === path) return true;
  if (normalizePath(brand.successPath) === path) return true;

  if (!brand.claimsModuleRoute || !brand.moduleRoute) return false;
  // Segment boundary, not a bare prefix: "/iqverity" must not swallow
  // "/iqverityfoo", which is a different route belonging to nobody.
  const route = normalizePath(brand.moduleRoute);
  return path === route || path.startsWith(`${route}/`);
}

/** Resolve which brand's experience a given location is in.
 *
 *  Two passes, hosts first. Interleaving them (one brand's host, then its path,
 *  then the next brand's) let array order beat the domain: on app.mytosp.com a
 *  /caddiegames path resolved to CaddieGames purely because CaddieGames is
 *  listed earlier. A custom domain is the stronger signal and now always wins. */
export function resolveAppContextFor(
  hostname: string,
  pathname: string,
): AppContext {
  for (const brand of FRONTEND_BRANDS) {
    if (brand.isDefault) continue;
    if (brand.matchesHost && brand.matchesHost(hostname)) return brand.brand;
  }

  const path = normalizePath(pathname);
  for (const brand of FRONTEND_BRANDS) {
    if (brand.isDefault) continue;
    if (ownsPath(brand, path)) return brand.brand;
  }

  return "asksmart";
}

/** Resolve which brand's experience the current window is in. */
export function resolveAppContext(): AppContext {
  if (typeof window === "undefined") return "asksmart";
  return resolveAppContextFor(
    window.location.hostname,
    window.location.pathname,
  );
}

// A brand id from an untrusted source (backend JSON, a Keychain stash) or undefined.
export function toAppContext(value: unknown): AppContext | undefined {
  return FRONTEND_BRANDS.find((b) => b.brand === value)?.brand;
}
